"use client";

import { SkeletonBlock, useStylesConfigValue } from "@health/ui";
import { Check } from "lucide-react";
import { useState } from "react";
import { AuthInlineCard } from "./AuthInlineCard";
import { AuthModal } from "./AuthModal";

type Props = {
  input: Record<string, unknown>;
  output?: unknown;
  onSubmit: (result: unknown) => void;
  submitted?: boolean;
  demoMode?: boolean;
  modalOnly?: boolean;
};

/**
 * Auth widget. Reads `authMode` from styles-config (populated from
 * `experience.variant.settings.auth_mode` in `ChatPanel`).
 *
 * - `dismissible` (default) — modal close (X / backdrop / Esc) emits
 *   `{ kind: "dismissed", reason: <distinct reason> }` per the canonical
 *   `AuthWidgetOutcome` contract in `@health/sdk-client`. Server records
 *   the dismissal and advances graph state per `auth_required_before`.
 * - `blocking` — modal close paths are suppressed: AuthInlineCard hides the
 *   "skip" affordance, AuthModal renders without an X button and ignores
 *   Esc / backdrop. There is no UI path that produces `kind: "dismissed"`.
 *   The server is also a guard: even if a tampered client emits a dismiss,
 *   `processAuthWidgetOutputs` in `lib/server/http/auth-widget-handler.ts`
 *   rejects it.
 */
export function AuthWidget({
  input,
  output,
  onSubmit,
  submitted,
  modalOnly,
  demoMode = false,
}: Props) {
  const authMode = useStylesConfigValue("authMode");
  const isBlocking = authMode === "blocking";
  const [modalOpen, setModalOpen] = useState(!demoMode);
  const [targetScreen, setTargetScreen] = useState<"signin" | "signup">(
    input.screen === "signin" ? "signin" : "signup",
  );

  if (submitted) {
    const result = output as { action?: string; kind?: string; method?: string } | undefined;
    const isSignup = result?.action === "signup" || result?.method === "signup";
    return (
      <div className="bg-widget-bg rounded-2xl p-4 shadow-sm opacity-75">
        <div className="flex items-center gap-2 text-success">
          <Check size={18} />
          <span className="text-sm font-medium">
            {isSignup ? "Account created — let's continue" : "Welcome back — let's continue"}
          </span>
        </div>
      </div>
    );
  }

  const handleOpenModal = (screen: "signin" | "signup") => {
    setTargetScreen(screen);
    setModalOpen(true);
  };

  // Emit a canonical dismissed outcome with a distinct reason. In blocking
  // mode this path is unreachable — modal close is suppressed at the AuthModal
  // level — but we keep the helper consistent for future entry points
  // (timeout, navigation, etc.).
  const emitDismissed = (
    reason: "modal_close" | "backdrop_click" | "navigation" | "timeout" | "cancel_button",
  ) => {
    onSubmit({ kind: "dismissed", reason });
  };

  const handleModalClose = () => {
    if (isBlocking) {
      // Defense in depth: this should never fire in blocking mode because
      // the AuthModal's onOpenChange is suppressed, but if some future code
      // path triggers it, do nothing — the user must complete auth.
      return;
    }
    setModalOpen(false);
    setTimeout(() => {
      emitDismissed("modal_close");
    }, 150);
  };

  const handleModalSubmit = (result: unknown) => {
    onSubmit(result);
    setModalOpen(false);
  };

  return (
    <>
      {!modalOnly && (
        <AuthInlineCard onOpenModal={handleOpenModal} modalOpen={modalOpen} authMode={authMode} />
      )}

      <AuthModal
        open={modalOnly || modalOpen}
        onOpenChange={(open) => {
          if (!open) handleModalClose();
        }}
        screen={targetScreen}
        input={input}
        output={output}
        onSubmit={handleModalSubmit}
        submitted={submitted}
        authMode={authMode}
      />
    </>
  );
}

export const AuthWidgetSkeleton = () => <SkeletonBlock $h="sm" />;
